import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type UIEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

interface PickerListProps extends ComponentPropsWithoutRef<'ul'> {
  ariaLabel?: string;
}

const PickerList = forwardRef<HTMLUListElement, PickerListProps>(({ ariaLabel, ...props }, ref) => {
  if (ariaLabel) {
    // A listbox is valid on ul; retaining ul keeps the wheel picker's existing layout intact.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role, local/ada-a11y-guard
    return <ul ref={ref} {...props} role="listbox" aria-label={ariaLabel} tabIndex={0} />;
  }

  return <ul ref={ref} {...props} />;
});

PickerList.displayName = 'PickerList';

// Cross-browser compatible UUID generation
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export default function Picker({
  optionHeight = 44,
  defaultValue,
  renderOption,
  visibleOptionNum = 5,
  options = [],
  className = '',
  ariaLabel,
  onChange = () => {},
  optionsFieldNames = {
    label: 'label',
    value: 'value',
  },
}: {
  optionHeight?: number | string;
  defaultValue?: any;
  renderOption?: (option: any, index: number) => React.ReactNode;
  visibleOptionNum?: number;
  options: any[];
  optionsFieldNames?: {
    label: string;
    value: string;
  };
  onChange?: (value?: any, option?: any, currentIndex?: number) => void;
  className?: string;
  ariaLabel?: string;
}) {
  const [visibleOptionNumber] = useState(
    visibleOptionNum % 2 !== 0 ? visibleOptionNum : visibleOptionNum + 1,
  );
  const [UUID] = useState(generateUUID());
  const [itemHeight] = useState<string>(
    typeof optionHeight === 'number' ? `${optionHeight}px` : (optionHeight as string),
  );
  const setEmptyArr = () => {
    const arr: number[] = [];
    for (let i = 0; i < (visibleOptionNumber - 1) / 2; i++) {
      arr.push(i);
    }
    return arr;
  };
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emptyItems] = useState<number[]>(setEmptyArr());
  const scrollTo = (value: any) => {
    if (scrollRef) {
      let index = 0;
      for (const i in options) {
        if (options[i][optionsFieldNames.label] === value) {
          index = Number(i);
          setCurrentIndex(index);
          break;
        }
      }
      const _itemHeight = document.getElementById(`picker-option-0-${UUID}`)?.offsetHeight || 44;
      const scrollTop = index * _itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  };
  let timer = null;
  const onScrollHandle = (event: UIEvent<HTMLUListElement>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      const { target } = event;
      const _itemHeight = document.getElementById(`picker-option-0-${UUID}`)?.offsetHeight || 44;
      let index = Math.floor((target as HTMLUListElement).scrollTop / _itemHeight);
      if (index < 0) index = 0;
      if (index > options.length - 1) index = options.length - 1;
      setCurrentIndex(index);
      onChange(options[index][optionsFieldNames.value], options[index], index);
    }, 100);
  };
  const selectIndex = (nextIndex: number) => {
    const clampedIndex = Math.max(0, Math.min(options.length - 1, nextIndex));
    if (!options[clampedIndex]) return;

    const _itemHeight = document.getElementById(`picker-option-0-${UUID}`)?.offsetHeight || 44;
    setCurrentIndex(clampedIndex);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = clampedIndex * _itemHeight;
    }
    onChange(options[clampedIndex][optionsFieldNames.value], options[clampedIndex], clampedIndex);
  };
  const onKeyDownHandle = (event: KeyboardEvent<HTMLUListElement>) => {
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex += 1;
    else if (event.key === 'ArrowUp') nextIndex -= 1;
    else if (event.key === 'PageDown') nextIndex += visibleOptionNumber;
    else if (event.key === 'PageUp') nextIndex -= visibleOptionNumber;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = options.length - 1;
    else return;

    event.preventDefault();
    selectIndex(nextIndex);
  };
  const scrollRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    scrollTo(defaultValue);
  }, []);
  return (
    <div className={`picker-container pl4 pr4 relative ${className}`}>
      <PickerList
        ref={scrollRef}
        ariaLabel={ariaLabel}
        id={`Picker-scroll-${UUID}`}
        className="picker-container-inner overflow-auto snap-y snap-mandatory scrollbar-hidden focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-purple"
        aria-activedescendant={ariaLabel ? `picker-option-${currentIndex}-${UUID}` : undefined}
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * visibleOptionNumber}${itemHeight.replace(/[0-9]/g, '')}`,
          paddingBottom: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
        onScroll={onScrollHandle}
        onKeyDown={ariaLabel ? onKeyDownHandle : undefined}
      >
        {emptyItems.map((item) => (
          <li
            key={`empty-${item}`}
            style={{
              height: itemHeight,
            }}
            className="snap-start w-full block"
            role={ariaLabel ? 'presentation' : undefined}
            aria-hidden={ariaLabel ? true : undefined}
          />
        ))}
        {options.map((option, index) => (
          <li
            style={{
              height: itemHeight,
            }}
            key={option[optionsFieldNames.label]}
            id={`picker-option-${index}-${UUID}`}
            className={`snap-start w-full overflow-hidden ${currentIndex === index && 'active bg-[#f3f6f7]'} current-value-picker`}
            data-value={option[optionsFieldNames.label]}
            role={ariaLabel ? 'option' : undefined}
            aria-selected={ariaLabel ? currentIndex === index : undefined}
          >
            {renderOption ? (
              renderOption(option, index)
            ) : (
              <div
                className={`pt-8 pb-8 flex text-center items-center justify-center text-16 ${currentIndex === index ? 'text-[18px]' : ''}`}
                style={{
                  height: itemHeight,
                }}
              >
                {option[optionsFieldNames.label]}
              </div>
            )}
          </li>
        ))}
      </PickerList>
      <div
        className="absolute top-0 left-0 right-0 bg-primary-white opacity-80 pointer-events-none"
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 bg-primary-white opacity-80 pointer-events-none"
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
      />
    </div>
  );
}
